import { useEffect, useState } from 'react'

/**
 * Cookies / analytics consent notice.
 *
 * The site itself stores only a few short strings in localStorage
 * (pb-theme, pb-lang, pb-analytics-consent) and no HTTP cookies of its own,
 * so that part is "strictly necessary" and needs no consent. GA4 also runs
 * cookieless (client_storage:'none' in index.html).
 *
 * Everything that sets a tracking cookie or an ID - MyDart, the NEO chat
 * widget, Google Tag Manager and Microsoft Clarity - is CONSENT-GATED by
 * /pb-consent.js (loaded in <head> on every page, including the standalone
 * home page, which draws its own copy of this notice). Nothing of theirs
 * loads for Decline or before a choice; this component only asks, and tells
 * the gate (window.pbConsent) what the visitor answered. The choice is
 * remembered in pb-analytics-consent ('granted' | 'denied') so the banner
 * shows once per browser.
 *
 * Defensive design: every localStorage call is wrapped in try/catch - Safari
 * Private Browsing and locked-down enterprise builds throw on storage writes,
 * and we don't want a thrown DOMException to break render or re-show the
 * banner every load.
 */
const CONSENT_KEY = 'pb-analytics-consent'

function safeRead() {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

function safeWrite(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value)
  } catch {
    /* storage blocked - accept silently; the banner reappears next visit */
  }
}

export default function CookieNotice() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const consent = safeRead()
    // Returning visitor who already chose: no banner (/pb-consent.js has already
    // loaded the vendors for 'granted' and kept them off for 'denied').
    if (consent === 'granted' || consent === 'denied') return
    // No choice yet - show the banner after the hero animation settles.
    const t = setTimeout(() => setVisible(true), 800)
    return () => clearTimeout(t)
  }, [])

  if (!visible) return null

  function accept() {
    if (window.pbConsent) window.pbConsent.grant()
    else safeWrite('granted')
    setVisible(false)
  }

  function decline() {
    if (window.pbConsent) window.pbConsent.deny()
    else safeWrite('denied')
    setVisible(false)
  }

  return (
    <div className="pb-cookie-notice" role="dialog" aria-label="Cookies notice" aria-live="polite">
      <p className="pb-cookie-notice-text">
        We store your theme and language as browser-side preferences. With your
        consent we also load the NEO chat assistant and analytics (Microsoft
        Clarity, MyDart and Google Tag Manager).{' '}
        <a href="/cookies-policy">Cookies policy →</a>
      </p>
      <div className="pb-cookie-notice-actions">
        <button
          type="button"
          onClick={decline}
          className="pb-cookie-notice-btn pb-cookie-notice-btn--ghost"
          aria-label="Decline analytics cookies"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={accept}
          className="pb-cookie-notice-btn"
          aria-label="Accept analytics cookies"
        >
          Accept
        </button>
      </div>
    </div>
  )
}
